<script>
  import Page from '@components/page.svelte'
  import Table from '@components/table'
  import FilterBar from '@components/table/filter-bar.svelte'
  import Dropdown from '@components/general/dropdown.svelte'
  import BoxedText from '@components/general/boxed-text.svelte'
  import Button from '@components/buttons/button.svelte'
  import ManageModal from '@components/feedbacks/manage-modal.svelte'
  import SentimentText from '@components/feedbacks/sentiment-text.svelte'
  import { usePageParam } from '@hooks/use-page-param.svelte'
  import { useSelection } from '@hooks/use-selection.svelte'
  import { router } from '@inertiajs/svelte'
  import { CheckCircle, CircleX, Eye, EyeOff, EllipsisVertical, Trash2, Wand, FileText, Video, CirclePlay, Layers } from 'lucide-svelte'
  import { formatDate, formatDateTime } from '@lib/utils/date'
  import { sortItems } from '@lib/utils'

  let { feedback = [], pagination, ids = [] } = $props()

  let selection = $derived(useSelection({ initialItems: feedback, allIds: ids }))
  let sort = usePageParam({ key: "sort", defaultValue: "newest", items: sortItems })

  let readStatus = usePageParam({ key: "read_status", defaultValue: "", items: [
    { value: "", label: "Status: All", icon: Eye },
    { value: "unread", label: "Status: Unread", icon: EyeOff },
    { value: "read", label: "Status: Read", icon: Eye },
  ] })

  let typeFilter = usePageParam({ key: "feedbackable_type", defaultValue: "", items: [
    { value: "", label: "Type: All", icon: Layers },
    { value: "LiveSession", label: "Type: Live Session", icon: Video },
    { value: "Video", label: "Type: Video", icon: CirclePlay },
    { value: "Resource", label: "Type: Resource", icon: FileText },
  ] })

  let sentimentHints = {
    positive: "Feedback was positive",
    negative: "Feedback was negative",
    neutral: "Feedback was neutral",
  }

  let id = $state(null)

  let selectedFeedback = $derived(feedback.find(feedback => feedback.id === id))

  function markRead(feedback) {
    if (feedback.read_at) return
    router.patch(`/admin/feedback/${feedback.id}/mark_read`)
  }

  function markUnread(feedback) {
    if (!feedback.read_at) return
    router.patch(`/admin/feedback/${feedback.id}/mark_unread`)
  }

  function deleteFeedback(feedback) {
    if (!window.confirm("Are you sure you want to delete this feedback?")) return
    router.delete(`/admin/feedback/${feedback.id}`)
  }

  function batchMarkRead() {
    router.post('/admin/feedback/batch_mark_read', { ids: selection.ids }, {
      onSuccess: selection.clear()
    })
  }

  function batchMarkUnread() {
    router.post('/admin/feedback/batch_mark_unread', { ids: selection.ids }, {
      onSuccess: selection.clear()
    })
  }

  function batchDelete() {
    if (!window.confirm(`Delete ${selection.count} selected feedback?`)) return
    router.delete('/admin/feedback/batch_delete', { data: { ids: selection.ids }, onSuccess: selection.clear() })
  }

</script>

<Page title="Feedback" subtitle="Manage your feedback" links={[{ text: "Admin", href: "/admin" }]}>

  <FilterBar {pagination} paginationProps={{ summary: true, terms: ["feedback", "feedback"] }}>
    {#snippet filters()}

      <!-- Sort -->
      <Dropdown buttonProps={{ text: sort.label || "Sort", size: "md", Icon: sort.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each sort.items as sortItem (sortItem.value)}
            {@render item({ text: sortItem.label, icon: sortItem.icon, onSelect: () => { selection.clear(); sort.setValue(sortItem.value) } })}
          {/each}
        {/snippet}
      </Dropdown>

      <!-- Read status filter -->
      <Dropdown buttonProps={{ text: readStatus.label || "Status", size: "md", Icon: readStatus.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each readStatus.items as statusItem (statusItem.value)}
            {@render item({ text: statusItem.label, icon: statusItem.icon, onSelect: () => { selection.clear(); readStatus.setValue(statusItem.value) } })}
          {/each}
        {/snippet}
      </Dropdown>

      <!-- Type filter -->
      <Dropdown buttonProps={{ text: typeFilter.label || "Type", size: "md", Icon: typeFilter.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each typeFilter.items as typeItem (typeItem.value)}
            {@render item({ text: typeItem.label, icon: typeItem.icon, onSelect: () => { selection.clear(); typeFilter.setValue(typeItem.value) } })}
          {/each}
        {/snippet}
      </Dropdown>

      <!-- Batch actions -->
      {#if selection?.populated}
        <Dropdown buttonProps={{ text: `${selection.count} selected`, size: "md", Icon: Wand }} width="16rem" align="start" itemSize="sm">
          {#snippet contents(item)}
            {@render item({ text: "Mark as read", icon: Eye, onSelect: batchMarkRead })}
            {@render item({ text: "Mark as unread", icon: EyeOff, onSelect: batchMarkUnread })}
            {@render item({ text: "Delete", icon: Trash2, onSelect: batchDelete })}
          {/snippet}
        </Dropdown>
      {/if}

    {/snippet}
  </FilterBar>

  <Table.Root empty={feedback.length === 0} emptyMessage="No feedback found">
    <Table.Head>
      <Table.SelectionDropdownCell {selection} />
      <Table.Header width="10%">Date</Table.Header>
      <Table.Header width="15%">User</Table.Header>
      <Table.Header width="15%">Type</Table.Header>
      <Table.Header width="22%">Record</Table.Header>
      <Table.Header width="8%" align="center">Opinion</Table.Header>
      <Table.Header width="10%" align="center">Read</Table.Header>
      <Table.Header width="10%" align="center">Actions</Table.Header>
    </Table.Head>
    <Table.Body>
      {#each feedback as feedback (feedback.id)}
        <Table.Row>
          <Table.Cell width="3rem" align="center">
            <input type="checkbox" checked={selection.has(feedback.id)} onclick={e => selection.toggle(feedback.id, e)} />
          </Table.Cell>
          <Table.Cell width="10%">
            <Button variant="link" text={formatDate(feedback.created_at)} onclick={() => id = feedback.id} />
          </Table.Cell>
          <Table.Cell width="15%">
            <Button variant="link" text={feedback.user.name} path={`/admin/users/${feedback.user.id}`} />
          </Table.Cell>
          <Table.Cell width="15%">{feedback.feedbackable_type}</Table.Cell>
          <Table.Cell width="22%">
            {#if feedback.record}
              <Button variant="link" path={feedback.record.path} text={feedback.record.name || "View"} />
            {:else}
              <span>-</span>
            {/if}
          </Table.Cell>
          <Table.Cell width="8%" align="center">
            <SentimentText sentiment={feedback.sentiment} showText={false} class="justify-center" hint={sentimentHints[feedback.sentiment]} />
          </Table.Cell>
          <Table.Cell width="10%" align="center">
            {#if feedback.read_at}
              <BoxedText variant="success" size="xs" text="Read" />
            {:else}
              <BoxedText variant="warning" size="xs" text="Unread" />
            {/if}
          </Table.Cell>
          <Table.Cell width="10%" align="center">
            <Dropdown>
              {#snippet trigger({ props })}
                <Button variant="naked" Icon={EllipsisVertical} iconProps={{ size: 16 }} {...props} />
              {/snippet}
              {#snippet contents(item)}
                {@render item({ text: "Mark as read", icon: CheckCircle, disabled: feedback.read_at, onSelect: () => markRead(feedback) })}
                {@render item({ text: "Mark as unread", icon: CircleX, disabled: !feedback.read_at, onSelect: () => markUnread(feedback) })}
                {@render item({ text: "Delete", icon: Trash2, onSelect: () => deleteFeedback(feedback) })}
              {/snippet}
            </Dropdown>
          </Table.Cell>
        </Table.Row>
      {/each}
    </Table.Body>
  </Table.Root>

  <Table.Pagination {pagination} paginationProps={{ summary: true, terms: ["feedback", "feedback"] }} />
  
</Page>

<ManageModal bind:open={id} feedback={selectedFeedback} {markRead} {markUnread} />